// src/pages/billing/JournalEntryDetailPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useBilling, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  ErrorAlert,
} from '@react/components';

function formatMoney(money: any): string {
  if (!money) return '--';
  const amount = (money.amount ?? 0) / Math.pow(10, money.decimal_places ?? 2);
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(dateStr); }
}

function statusVariant(status: string): 'success' | 'warning' | 'error' | 'primary' | 'secondary' {
  switch (status) {
    case 'JOURNAL_ENTRY_STATUS_POSTED': return 'success';
    case 'JOURNAL_ENTRY_STATUS_PENDING': return 'warning';
    case 'JOURNAL_ENTRY_STATUS_DRAFT': return 'secondary';
    case 'JOURNAL_ENTRY_STATUS_VOIDED': return 'error';
    default: return 'secondary';
  }
}

function enumLabel(value: string, prefix: string): string {
  return (value || '').replace(prefix, '').replace(/_/g, ' ');
}

export const JournalEntryDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useBilling();
  const { t } = useI18n();
  const [actionLoading, setActionLoading] = useState('');
  const [item, setItem] = useState<any>(null);

  const loadEntry = (entryId: string) => {
    store.GetJournalEntry(entryId)
      .then((res) => setItem(res?.journal_entry ?? null))
      .catch(() => {});
  };

  useEffect(() => {
    if (id) loadEntry(id);
    return () => { setItem(null); };
  }, [id]);

  const handleAction = async (action: string) => {
    if (!id) return;
    setActionLoading(action);
    try {
      if (action === 'post') await store.PostJournalEntry(id, {});
      else if (action === 'void') await store.VoidJournalEntry(id, {});
      loadEntry(id);
    } catch {
      // Error handled by store
    } finally {
      setActionLoading('');
    }
  };

  if (store.isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;
  }
  if (store.error) {
    return <><ErrorAlert error={store.error} /><Link to="/billing/journal-entries" className="mt-2 inline-block text-sm underline text-primary mt-2 inline-block text-sm underline text-primary">{t("common.backToList")}</Link></>;
  }
  if (!item) {
    return <Alert variant="warning" title={t('common.notFound')}><Link to="/billing/journal-entries" className="mt-2 inline-block text-sm underline text-primary">{t('common.backToList')}</Link></Alert>;
  }

  const isDraft = item.status === 'JOURNAL_ENTRY_STATUS_DRAFT' || item.status === 'JOURNAL_ENTRY_STATUS_PENDING';
  const isPosted = item.status === 'JOURNAL_ENTRY_STATUS_POSTED';

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/billing/journal-entries" className="text-muted-foreground hover:text-foreground">Journal Entries</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{String(item.number ?? id)}</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">Journal Entry {String(item.number ?? '')}</h1>
            <Badge variant={statusVariant(item.status)} size="sm">{enumLabel(item.status, 'JOURNAL_ENTRY_STATUS_')}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{enumLabel(item.type, 'JOURNAL_ENTRY_TYPE_')} &middot; {String(item.description ?? '')}</p>
        </div>
        <div className="flex gap-2">
          {isDraft && (
            <Button variant="primary" onClick={() => handleAction('post')} disabled={actionLoading === 'post'}>
              {actionLoading === 'post' ? 'Posting...' : 'Post to Ledger'}
            </Button>
          )}
          {isPosted && (
            <Button variant="danger" onClick={() => handleAction('void')} disabled={actionLoading === 'void'}>
              {actionLoading === 'void' ? 'Voiding...' : 'Void Entry'}
            </Button>
          )}
        </div>
      </div>

      {/* Entry info */}
      <Card>
        <CardHeader><CardTitle>Entry Details</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Entry Date</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.entry_date)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Period</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.period_start)} - {formatDate(item.period_end)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Memo</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.memo ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Source</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.source_type ?? '--')} {item.source?.id ? `(${item.source.id})` : ''}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.created_at)}</dd>
            </div>
            {item.posted_at && (
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">Posted</dt>
                <dd className="text-sm text-foreground col-span-2">{formatDate(item.posted_at)}</dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {/* Lines */}
      <Card>
        <CardHeader><CardTitle>Journal Lines</CardTitle></CardHeader>
        <CardContent>
          <Table striped>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Credit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(item.lines?.lines ?? []).map((line: any, i: number) => (
                <TableRow key={line.id || i}>
                  <TableCell className="font-mono">{String(line.account?.id ?? '--')}</TableCell>
                  <TableCell>{String(line.description ?? '')}</TableCell>
                  <TableCell className="text-right">{line.debit ? formatMoney(line.debit) : ''}</TableCell>
                  <TableCell className="text-right">{line.credit ? formatMoney(line.credit) : ''}</TableCell>
                </TableRow>
              ))}
              <TableRow className="font-bold border-t-2">
                <TableCell colSpan={2} className="text-right">Total</TableCell>
                <TableCell className="text-right">
                  {formatMoney({ amount: (item.lines?.lines ?? []).reduce((s: number, l: any) => s + (l.debit?.amount ?? 0), 0), decimal_places: 2 })}
                </TableCell>
                <TableCell className="text-right">
                  {formatMoney({ amount: (item.lines?.lines ?? []).reduce((s: number, l: any) => s + (l.credit?.amount ?? 0), 0), decimal_places: 2 })}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
});

export default JournalEntryDetailPage;
